import Link from "next/link";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useRouter } from "next/router";

import { BRAND_PATHS } from "@chef/constants";
import { Button, Card, Input } from "@chef/components";
import {
  getStore,
  IUpdateUserInfoArgs,
  UserInfoQuery,
  useUpdateUserInformationMutation,
  useUserInfoQuery,
  validateApi,
} from "@chef/state-management";
import { useExtendedForm } from "@chef/hooks";
import { isValidEmail } from "@chef/utils/emailValidation";
import { isValidTelephoneNumber } from "@chef/utils/telephoneValidation";
import { Loading } from "@chef/icons/small";

import { MyAccountLayout } from "../../components/MyAccountLayout";

import { intl } from "./MyAccountChangePersonalDetailsPage.Intl";

const createSchema: (args: {
  initialEmail?: string;
  initialPhone?: string;
}) => yup.SchemaOf<Omit<IUpdateUserInfoArgs, "telephoneAlt">, never> = (args) =>
  yup.object().shape({
    firstName: yup.string().required(intl.REQUIRED_FIELD),
    lastName: yup.string().required(intl.REQUIRED_FIELD),
    email: yup
      .string()
      .required(intl.REQUIRED_FIELD)
      .test({
        test: (value) => isValidEmail(value || ""),
        message: intl.MUST_BE_VALID_EMAIL,
      })
      .test({
        test: async (value) => {
          // We need to duplicate the check here because yup runs validations asynchronously
          if (
            value === undefined ||
            !isValidEmail(value) ||
            value === args.initialEmail
          ) {
            return true;
          }

          try {
            await getStore()
              .dispatch(
                validateApi.endpoints.validateEmailInUse.initiate({
                  email: value,
                }),
              )
              .unwrap();
            return true;
          } catch (error) {
            console.error(error);
            return false;
          }
        },

        message: intl.EMAIL_IN_USE,
      }),

    telephone: yup
      .string()
      .matches(/^[0-9]*$/, intl.CAN_ONLY_CONTAIN_NUMBERS)
      .required(intl.REQUIRED_FIELD)
      .test({
        test: (value) => isValidTelephoneNumber(value || ""),
        message: intl.MUST_BE_EXACTLY_DIGITS,
      })
      .test({
        test: async (value) => {
          // We need to duplicate the check here because yup runs validations asynchronously
          if (
            value === undefined ||
            !isValidTelephoneNumber(value) ||
            value === args.initialPhone
          ) {
            return true;
          }

          try {
            await getStore()
              .dispatch(
                validateApi.endpoints.validateTelephoneInUse.initiate({
                  telephone: value,
                }),
              )
              .unwrap();
            return true;
          } catch (error) {
            console.error(error);
            return false;
          }
        },

        message: intl.TELEPHONE_NUMBER_ALREADY_IN_USE,
      }),
  });

export const MyAccountChangePersonalDetailsPage = () => {
  const { data: userInfoQuery, isFetching: isLoadingInitialUserInfo } =
    useUserInfoQuery();

  if (isLoadingInitialUserInfo) {
    return <Loading />;
  }

  return <MyPersonalDetailsForm initialUserInfo={userInfoQuery?.userInfo} />;
};

const MyPersonalDetailsForm = ({
  initialUserInfo,
}: {
  initialUserInfo?: UserInfoQuery["userInfo"];
}) => {
  const { register, handleSubmit } = useExtendedForm<IUpdateUserInfoArgs>({
    defaultValues: initialUserInfo,
    resolver: yupResolver(
      createSchema({
        initialEmail: initialUserInfo?.email,
        initialPhone: initialUserInfo?.telephone,
      }),
    ),
    mode: "all",
  });
  const router = useRouter();

  const [
    updateUserInformationMutation,
    { isLoading: isUpdatingUserInformation },
  ] = useUpdateUserInformationMutation();

  const onSubmit = async (formData: IUpdateUserInfoArgs) => {
    try {
      await updateUserInformationMutation(formData).unwrap();

      router.push(BRAND_PATHS.PERSONAL_INFORMATION_PAGE);
    } catch (err) {
      console.error(err);
    }
  };
  return (
    <MyAccountLayout
      page="accountInformation"
      title={intl.PERSONAL_INFORMATION}
    >
      <section className="col-span-5 space-y-6">
        <Card id="min-konto-rediger-personalia-card-grid">
          <form
            className="flex flex-col gap-8"
            onSubmit={handleSubmit(onSubmit)}
          >
            <Input
              {...register("firstName")}
              label={intl.FIRST_NAME_FORM_LABEL}
            />
            <Input
              {...register("lastName")}
              label={intl.LAST_NAME_FORM_LABEL}
            />
            <Input {...register("email")} label={intl.EMAIL_FORM_LABEL} />
            <Input
              {...register("telephone")}
              label={intl.TELEPHONE_FORM_LABEL}
            />
            <div className="flex flex-col gap-4 lg:flex-row-reverse lg:self-end">
              <Button
                loading={isUpdatingUserInformation}
                primary
                id="submit-change-account-info-button"
                type="submit"
              >
                {intl.SUBMIT_BUTTON_TEXT}
              </Button>

              <Link
                href={BRAND_PATHS.PERSONAL_INFORMATION_PAGE}
                passHref
                legacyBehavior
              >
                <Button
                  disabled={isUpdatingUserInformation}
                  outlined
                  id="cancel-change-account-info-button"
                >
                  {intl.CANCEL_BUTTON_TEXT}{" "}
                </Button>
              </Link>
            </div>
          </form>
        </Card>
      </section>
    </MyAccountLayout>
  );
};
